import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Sparkles } from "lucide-react";
import { adminGetFreeTier, adminSetFreeTier, AI_PROVIDER_OPTIONS } from "../../../lib/api";

export function FreeTierCard() {
    const [cfg, setCfg] = useState({
        enabled: false,
        credits: 0,
        provider: "",
    });
    const [initial, setInitial] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const c = await adminGetFreeTier();
                setCfg(c);
                setInitial(c);
            } catch (e) {
                toast.error(
                    e?.response?.data?.detail || "Couldn't load free-tier config",
                );
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const dirty = JSON.stringify(cfg) !== JSON.stringify(initial);

    const save = async () => {
        setSaving(true);
        try {
            const next = await adminSetFreeTier({
                enabled: !!cfg.enabled,
                credits: Math.max(parseInt(cfg.credits || 0, 10) || 0, 0),
                provider: cfg.provider || "",
            });
            setCfg(next);
            setInitial(next);
            toast.success("Free-tier settings saved");
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Couldn't save",
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <section
            className="rounded-md border border-rose-500/40 bg-rose-500/5 p-5"
            data-testid="admin-free-tier-card"
        >
            <header className="flex items-start justify-between gap-3 mb-3">
                <div>
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-rose-300 font-bold">
                        <Sparkles className="w-3.5 h-3.5" />
                        Free tier
                    </div>
                    <h3 className="mt-1 text-lg sm:text-xl font-black tracking-tight text-white">
                        What users without a subscription get
                    </h3>
                    <p className="mt-1 text-[12px] text-rose-100/80 max-w-prose">
                        Credits below reset on the 1st of each calendar month.
                        Turn the toggle off to hard-paywall AI features and force everyone to subscribe.
                    </p>
                </div>
                <label
                    className="flex items-center gap-2 cursor-pointer select-none"
                    data-testid="free-tier-enable-label"
                >
                    <input
                        type="checkbox"
                        checked={!!cfg.enabled}
                        onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
                        data-testid="free-tier-enable"
                        className="w-4 h-4 rounded-sm accent-rose-500"
                    />
                    <span className="text-[12px] font-bold text-rose-100">
                        {cfg.enabled ? "Enabled" : "Disabled"}
                    </span>
                </label>
            </header>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-rose-200 block mb-1">
                        Credits per month
                    </label>
                    <input
                        type="number"
                        min="0"
                        value={cfg.credits}
                        onChange={(e) => setCfg({ ...cfg, credits: e.target.value })}
                        disabled={loading || !cfg.enabled}
                        data-testid="free-tier-credits"
                        className="w-full px-3 py-2 bg-[#0A1128] border border-rose-500/30 rounded-sm text-white text-sm outline-none focus:border-rose-400 disabled:opacity-50"
                    />
                </div>
                <div className="sm:col-span-2">
                    <label className="text-[10px] uppercase tracking-wider font-bold text-rose-200 block mb-1">
                        AI provider for free users
                    </label>
                    <select
                        value={cfg.provider || ""}
                        onChange={(e) => setCfg({ ...cfg, provider: e.target.value })}
                        disabled={loading || !cfg.enabled}
                        data-testid="free-tier-provider"
                        className="w-full px-3 py-2 bg-[#0A1128] border border-rose-500/30 rounded-sm text-white text-sm outline-none focus:border-rose-400 disabled:opacity-50"
                    >
                        {AI_PROVIDER_OPTIONS.map((opt) => (
                            <option key={opt.id || "default"} value={opt.id}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="mt-4 flex items-center justify-end gap-2">
                {dirty && !saving && (
                    <span className="text-[11px] text-amber-300 font-bold">
                        Unsaved changes
                    </span>
                )}
                <button
                    type="button"
                    onClick={save}
                    disabled={!dirty || saving}
                    data-testid="free-tier-save"
                    className="px-4 py-2 text-sm font-bold rounded-sm bg-rose-500 hover:bg-rose-400 text-white disabled:opacity-40"
                >
                    {saving ? "Saving…" : "Save free-tier settings"}
                </button>
            </div>
        </section>
    );
}


// ----- iter-95: Credit Packs admin -----

